import {
  Button,
  DialogActions,
  Grid,
  Table,
  TableContainer,
} from '@material-ui/core';
import moment from 'moment';
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ConfirmationDialog from '../../../../../components/ConfirmationDialog';
import CustomDialog, {
  CustomDialogProps,
} from '../../../../../components/CustomDialog/index';
import CustomTableHead, {
  HeadCell,
} from '../../../../../components/CustomTableHead';
import CustomTablePagination from '../../../../../components/CustomTablePagination/CustomTablePagination';
import SelectPeriod from '../../../../../components/SelectPeriod/index';
import TableSkeleton from '../../../../../components/TableSkeleton';
import { useLoadingDialog } from '../../../../../hooks/backdrop';
import { useFeedback } from '../../../../../hooks/feedback';
import { BatchSigningPunchCardDTO } from '../../../../../models/dto/BatchSigningPunchCardDTO';
import {
  DefaultWorkShift,
  EmployeePunchCardDTO,
  OccurrencesControl,
} from '../../../../../models/dto/EmployeePunchCardDTO';
import { PeriodDTO } from '../../../../../models/dto/PeriodDTO';
import { ReferencePunchCardDTO } from '../../../../../models/dto/ReferencePunchCardDTO';
import { PENDING_OCCURRENCES_DESCRIPTION } from '../../../../../models/enum/PeriodOccurrencesEnum';
import { api } from '../../../../../services/api';
import { AppState } from '../../../../../store';
import { getReferencePunchCard } from '../../../../../store/actions/punchCardActions';
import { CondominiumReducerData } from '../../../../../store/reducers/condominiumReducer';
import { updateArrayItem } from '../../../../../utils/array.util';
import { isNullOrEmpty } from '../../../../../utils/validation.util';
import { AllowanceDiscount } from '../EmployeePunchCard/EmployeePunchCardDialog';
import BatchSigningPunchCardTableBody, {
  BatchSigningPunchCardTableData,
} from './BatchSigningPunchCardTableBody';

interface BatchSigningPunchCardRequestData {
  id: number;
  flagAprovado: boolean;
}

export interface FormattedBatchFormsData {
  occurrencesControl: OccurrencesControl[];
  defaultWorkShifts: DefaultWorkShift[];
}

const BatchSigningPunchCardDialog: React.FC<CustomDialogProps> = ({
  open,
  title,
  handleClose,
  handleConfirm,
}) => {
  const { handleLoadingDialog } = useLoadingDialog();
  const { addFeedback } = useFeedback();
  const [signingPunchCards, setSigningPunchCards] = useState<
    BatchSigningPunchCardDTO[]
  >([]);
  const [tableData, setTableData] = useState<BatchSigningPunchCardTableData[]>(
    [],
  );
  const [selectedItems, setSelectedItems] = useState<
    BatchSigningPunchCardTableData[]
  >([]);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<string>('employeeName');
  const [periodSelected, setPeriodSelected] = useState<PeriodDTO | null>(null);
  const [loadingSignings, setLoadingSignings] = useState<boolean>(false);
  const [changedOccurrences, setChangedOccurrences] = useState<
    AllowanceDiscount[]
  >([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(7);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [confirmationDialogMessage, setConfirmationDialogMessage] = useState(
    '',
  );
  const [mainAction, setMainAction] = useState<'SIGN' | 'SAVE' | ''>('');

  const [referencePunchCard, setReferencePunchCard] = useState<
    ReferencePunchCardDTO[]
  >([]);
  const [loadingReferencePunchCard, setLoadingReferencePunchCard] = useState(
    false,
  );

  const { condominiumSelected }: CondominiumReducerData = useSelector(
    (state: AppState) => state.condominiumReducer,
  );

  const tableColumns: HeadCell[] = [
    {
      id: 'employeeName',
      numeric: false,
      disablePadding: false,
      label: 'Colaborador',
    },
    {
      id: 'employeeOffice',
      numeric: false,
      disablePadding: false,
      label: 'Cargo',
    },
    {
      id: 'totalPendingOccurrences',
      numeric: false,
      disablePadding: false,
      label: 'Ocorrências Pendentes',
    },
    {
      id: 'signingStatus',
      numeric: false,
      disablePadding: false,
      label: 'Status da Assinatura',
    },
    {
      id: 'lunchTime',
      numeric: false,
      disablePadding: false,
      label: 'Inserir Horário de Almoço',
      tooltip:
        'Marcando esta opção o sistema irá considerar também, o horário de almoço do funcionário para inserção automática do horário padrão.',
    },
    {
      id: 'ocurrencyControl',
      numeric: false,
      disablePadding: false,
      label: 'Ações',
    },
  ];

  const handleChangeAllowanceDiscount = (
    event: ChangeEvent<{ name?: string | undefined; value: unknown }>,
    employeeCode: string,
  ) => {
    event.stopPropagation();
    const valueSelected = String(event.target.value);
    const registerChanged = referencePunchCard.find(
      initial => String(initial.funcionario.numeroCadastro) === employeeCode,
    );

    if (registerChanged) {
      if (valueSelected === 'NENHUM') {
        setChangedOccurrences(
          changedOccurrences.filter(
            item => item.numeroCadastro !== employeeCode,
          ),
        );
      } else if (
        changedOccurrences.some(item => item.numeroCadastro === employeeCode)
      ) {
        const listUpdated = updateArrayItem(
          changedOccurrences,
          employeeCode,
          'numeroCadastro',
          { tipoControleOcorrencia: valueSelected },
        );
        setChangedOccurrences(listUpdated);
      } else {
        setChangedOccurrences([
          ...changedOccurrences,
          {
            tipoControleOcorrencia: valueSelected,
            numeroCadastro: employeeCode,
            nomeFuncionario: registerChanged.funcionario.nome,
            dataEfetivo: '',
          },
        ]);
      }
      if (!isNullOrEmpty(tableData)) {
        const listUpdated = updateArrayItem(
          tableData,
          employeeCode,
          'employeeCode',
          { occurrencyControl: valueSelected },
        );
        setTableData(listUpdated);
      }
    }
  };

  const handleCheckLunchTime = (checked: boolean, employeeCode: string) => {
    if (!isNullOrEmpty(changedOccurrences)) {
      const listUpdated = updateArrayItem(
        changedOccurrences,
        employeeCode,
        'numeroCadastro',
        { insereRefeicao: checked },
      );
      setChangedOccurrences(listUpdated);
    }
    if (!isNullOrEmpty(tableData)) {
      const listUpdated = updateArrayItem(
        tableData,
        employeeCode,
        'employeeCode',
        { lunchTime: checked },
      );
      setTableData(listUpdated);
    }
  };

  const handleSelectItem = (item: BatchSigningPunchCardTableData) => {
    const alreadySelected = selectedItems.findIndex(
      selected => selected.signingId === item.signingId,
    );

    if (alreadySelected >= 0) {
      const filteredItems = selectedItems.filter(
        selected => selected.signingId !== item.signingId,
      );
      setSelectedItems(filteredItems);
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const handleSelectAll = (isSelectAll: boolean) => {
    if (isSelectAll) {
      setSelectedItems(tableData);
    } else {
      setSelectedItems([]);
    }
  };

  const getTotalPendingOccurrencesByEmployee = useCallback(
    (employeePunchCard: EmployeePunchCardDTO[]): number => {
      let totalPendingOccurrences = 0;
      // eslint-disable-next-line no-unused-expressions
      employeePunchCard?.forEach(punchCard => {
        totalPendingOccurrences +=
          punchCard.ocorrencias?.filter(occurrence =>
            PENDING_OCCURRENCES_DESCRIPTION.some(item => item === occurrence),
          )?.length || 0;
      });
      return totalPendingOccurrences;
    },
    [],
  );

  const buildTableData = useCallback(
    (
      punchCardSignings: BatchSigningPunchCardDTO[],
      referencePunchCards: ReferencePunchCardDTO[],
    ) => {
      const builtTableData: BatchSigningPunchCardTableData[] = [];
      if (referencePunchCard.length > 0) {
        punchCardSignings.forEach(item => {
          const employeePunchCard = referencePunchCards.find(
            punchCard =>
              String(punchCard.funcionario.numeroCadastro) ===
              item.numeroCadastro,
          );

          if (employeePunchCard) {
            builtTableData.push({
              signingId: item.id,
              employeeCode: item.numeroCadastro,
              employeeName: employeePunchCard.funcionario.nome,
              employeeOffice: employeePunchCard.funcionario.cargo.descricao,
              totalPendingOccurrences: getTotalPendingOccurrencesByEmployee(
                employeePunchCard.pontos || [],
              ),
              signingStatus: item.flagAprovado,
              occurrencyControl: 'NENHUM',
            });
          }
        });
      }
      setTableData(builtTableData);
    },
    [getTotalPendingOccurrencesByEmployee, referencePunchCard.length],
  );

  const fillRegistersFormData = useCallback((): FormattedBatchFormsData => {
    const occurrencesControl: OccurrencesControl[] = [];
    const defaultWorkShifts: DefaultWorkShift[] = [];
    if (!isNullOrEmpty(referencePunchCard)) {
      changedOccurrences.forEach(changedOccurrence => {
        const employeePunchCard = referencePunchCard.find(
          item =>
            String(item.funcionario.numeroCadastro) ===
            changedOccurrence.numeroCadastro,
        );

        // eslint-disable-next-line no-unused-expressions
        employeePunchCard?.pontos?.forEach(punchCard => {
          const hasPendingOccurency = punchCard.ocorrencias?.some(occurrence =>
            PENDING_OCCURRENCES_DESCRIPTION.some(item => item === occurrence),
          );
          if (hasPendingOccurency) {
            if (changedOccurrence.tipoControleOcorrencia !== 'PADRAO') {
              occurrencesControl.push({
                dataEfetivo: punchCard.data,
                numeroCadastro: String(
                  employeePunchCard.funcionario.numeroCadastro,
                ),
                minutos: 0,
                referencia: employeePunchCard.funcionario.referencia,
                tipoControleOcorrencia:
                  changedOccurrence.tipoControleOcorrencia,
              });
            } else if (
              !defaultWorkShifts.some(
                item =>
                  item.numeroCadastro ===
                  employeePunchCard.funcionario.numeroCadastro,
              )
            ) {
              defaultWorkShifts.push({
                numeroCadastro: employeePunchCard.funcionario.numeroCadastro,
                justificativa: '',
                horarioPadrao: true,
                referencia: String(employeePunchCard.funcionario.referencia),
                insereRefeicao: changedOccurrence.insereRefeicao
                  ? 'sim'
                  : 'nao',
                periodoDeHorarioPadrao: moment(
                  periodSelected?.inicioMesApuracao,
                  'DD/MM/YYYY',
                ).format('DD/MM/YYYY'),
                periodoAteHorarioPadrao: moment(
                  periodSelected?.fimMesApuracao,
                  'DD/MM/YYYY',
                ).format('DD/MM/YYYY'),
              });
            }
          }
        });
      });
    }
    return { occurrencesControl, defaultWorkShifts };
  }, [changedOccurrences, referencePunchCard, periodSelected]);

  const handleDiscounts = useCallback(async () => {
    if (!isNullOrEmpty(changedOccurrences)) {
      const registersFormData: FormattedBatchFormsData = fillRegistersFormData();

      if (!isNullOrEmpty(registersFormData.occurrencesControl)) {
        await api.post(
          '/gdp/controle-ocorrencias',
          registersFormData.occurrencesControl,
        );
      }

      if (!isNullOrEmpty(registersFormData.defaultWorkShifts)) {
        await api.post('gdp/pontos/lote', registersFormData.defaultWorkShifts);
      }
    }
  }, [changedOccurrences, fillRegistersFormData]);

  const getEmployeesSignings = useCallback(async () => {
    setLoadingSignings(true);
    try {
      if (periodSelected && condominiumSelected) {
        const { data } = await api.get<BatchSigningPunchCardDTO[]>(
          `/gdp/assinaturas`,
          {
            params: {
              referencia: condominiumSelected?.referencia,
              tipoAssinatura: 'ESPELHO',
              dataPeriodo: moment(
                periodSelected?.mesFechamento,
                'DD/MM/YYYY',
              ).format('YYYY-MM-DD'),
            },
          },
        );
        if (data) {
          setSigningPunchCards(data);
        }
      }
    } catch (error) {
      console.log(error);
      setSigningPunchCards([]);
    } finally {
      setLoadingSignings(false);
    }
  }, [condominiumSelected, periodSelected]);

  const handleSignatures = useCallback(async () => {
    if (mainAction === 'SIGN') {
      const postData: BatchSigningPunchCardRequestData[] = [];
      selectedItems.forEach(item =>
        postData.push({
          id: item.signingId,
          flagAprovado: true,
        }),
      );
      await api.put(`/gdp/assinaturas/lotes`, postData);
    }
  }, [mainAction, selectedItems]);

  const handleSubmitAll = useCallback(async () => {
    try {
      handleLoadingDialog(true);
      setOpenConfirmationDialog(false);
      handleClose();
      await Promise.all([await handleDiscounts(), await handleSignatures()]);
      addFeedback({
        description: 'Alterações realizadas com sucesso',
        type: 'success',
      });
      await getEmployeesSignings();
      setSelectedItems([]);
      setChangedOccurrences([]);
    } catch (error) {
      addFeedback({
        type: 'error',
        description: 'Ocorreu um erro ao realizar as alterações!',
      });
    } finally {
      handleLoadingDialog(false);
    }
  }, [
    addFeedback,
    getEmployeesSignings,
    handleClose,
    handleDiscounts,
    handleLoadingDialog,
    handleSignatures,
  ]);

  const handleOpenConfirmationDialog = (clickSource: 'SIGN' | 'SAVE') => {
    setMainAction(clickSource);
    setConfirmationDialogMessage('Deseja realmente confirmar esta ação?\n');
    if (changedOccurrences.length > 0) {
      setConfirmationDialogMessage(
        previous =>
          `${previous} * Correção automática de ocorrência(s) pendente(s) do(s) funcionário(s):\n`,
      );
      changedOccurrences.forEach(changedOccurrence => {
        setConfirmationDialogMessage(
          previous => `${previous} -  ${changedOccurrence.nomeFuncionario}\n`,
        );
      });
    }
    if (clickSource === 'SIGN') {
      setConfirmationDialogMessage(
        previous =>
          `${previous} * Assinatura de ${
            selectedItems.length
          } espelho(s) de ponto do período de ${moment(
            periodSelected?.mesFechamento,
            'DD/MM/YYYY',
          ).format('MMMM / YYYY')}?\n`,
      );
    }
    setOpenConfirmationDialog(true);
  };

  useEffect(() => {
    const getSignings = async () => {
      await getEmployeesSignings();
      setSelectedItems([]);
    };
    getSignings();
  }, [getEmployeesSignings]);

  useEffect(() => {
    buildTableData(signingPunchCards, referencePunchCard);
  }, [referencePunchCard, buildTableData, signingPunchCards]);

  useEffect(() => {
    const getReferencePunchPeriodCard = async () => {
      try {
        setLoadingReferencePunchCard(true);
        const isPunchCardFromCurrentPeriodSelected = referencePunchCard.some(
          punchCard =>
            punchCard.pontos.some(item =>
              moment(item.data, 'YYYY-MM-DD').isSame(
                moment(periodSelected?.inicioMesApuracao, 'DD/MM/YYYY'),
              ),
            ),
        );
        if (periodSelected && !isPunchCardFromCurrentPeriodSelected) {
          const { data } = await getReferencePunchCard(
            moment(periodSelected.inicioMesApuracao, 'DD/MM/YYYY').format(
              'YYYY-MM-DD',
            ),
            moment(periodSelected.fimMesApuracao, 'DD/MM/YYYY').format(
              'YYYY-MM-DD',
            ),
            condominiumSelected?.referencia,
          );
          setReferencePunchCard(data);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingReferencePunchCard(false);
      }
    };
    getReferencePunchPeriodCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodSelected]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  return (
    <>
      <CustomDialog
        open={open}
        handleClose={handleClose}
        maxWidth="lg"
        fullWidth={false}
        keepMounted={false}
        contentDividers
        showCloseButton
        title={title}
        CustomDialogActionsComponent={
          <DialogActions style={{ justifyContent: 'space-between' }}>
            <Grid container item>
              <Button
                onClick={() => handleOpenConfirmationDialog('SIGN')}
                disabled={selectedItems.length === 0}
                variant="contained"
                color="primary"
              >
                Assinar ponto digital
              </Button>
            </Grid>
            <Grid container item justify="flex-end">
              <Button onClick={handleClose} color="inherit">
                Cancelar
              </Button>
              <Button
                onClick={() => handleOpenConfirmationDialog('SAVE')}
                disabled={changedOccurrences.length === 0}
                variant="contained"
                color="primary"
              >
                Salvar
              </Button>
            </Grid>
          </DialogActions>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SelectPeriod
              showLabel
              disableUnderline
              value={periodSelected}
              handlePeriodChange={setPeriodSelected}
            />
          </Grid>
        </Grid>

        {loadingReferencePunchCard || loadingSignings ? (
          <TableSkeleton rows={4} />
        ) : (
          <TableContainer>
            <Table>
              <CustomTableHead
                columns={tableColumns}
                order={order}
                orderBy={orderBy}
                hasSelection
                onRequestSort={handleRequestSort}
                handleSelectAll={handleSelectAll}
              />
              <BatchSigningPunchCardTableBody
                data={tableData}
                selectedItems={selectedItems}
                handleSelectItem={handleSelectItem}
                handleSelectActions={handleChangeAllowanceDiscount}
                handleCheckLunchTime={handleCheckLunchTime}
                order={order}
                orderBy={orderBy}
                page={page}
                rowsPerPage={rowsPerPage}
              />
            </Table>
            <CustomTablePagination
              onChangePage={handleChangePage}
              count={signingPunchCards.length}
              page={page}
              rowsPerPage={rowsPerPage}
            />
          </TableContainer>
        )}
      </CustomDialog>
      <ConfirmationDialog
        open={openConfirmationDialog}
        title="Atenção!"
        maxWidth="sm"
        onConfirm={handleSubmitAll}
        onCancel={() => setOpenConfirmationDialog(false)}
        handleClose={() => setOpenConfirmationDialog(false)}
        mainText={confirmationDialogMessage}
        enableMainTextLineBrake
      />
    </>
  );
};

export default BatchSigningPunchCardDialog;
